// Animated WebP from still WebP frames, as the canvas makes them. The
// layout is in https://developers.google.com/speed/webp/docs/riff_container
// (all numbers little-endian).

const fourccAt = (bytes, i) => String.fromCharCode(...bytes.subarray(i, i + 4))
const u32At = (bytes, i) =>
  (bytes[i] | (bytes[i + 1] << 8) | (bytes[i + 2] << 16)) +
  bytes[i + 3] * 0x1000000

const IMAGE_CHUNKS = ['ALPH', 'VP8 ', 'VP8L']

// The image chunks of a still WebP (ALPH and VP8, or VP8L) and whether it
// has alpha. Throws when it is not a WebP picture.
export const frameChunks = (bytes) => {
  if (fourccAt(bytes, 0) !== 'RIFF' || fourccAt(bytes, 8) !== 'WEBP') {
    throw new Error('not a WebP file')
  }
  const chunks = []
  // chunks are padded to an even size; the size field leaves the pad out
  for (let i = 12; i + 8 <= bytes.length; ) {
    const fourcc = fourccAt(bytes, i)
    const size = u32At(bytes, i + 4)
    if (IMAGE_CHUNKS.includes(fourcc)) {
      chunks.push({ fourcc, data: bytes.slice(i + 8, i + 8 + size) })
    }
    i += 8 + size + (size % 2)
  }
  const image = chunks.find((c) => c.fourcc !== 'ALPH')
  if (!image) throw new Error('no picture in the WebP file')
  // lossless keeps its own alpha: the bit after the 14-bit width and height
  const alpha =
    chunks.some((c) => c.fourcc === 'ALPH') ||
    (image.fourcc === 'VP8L' && (image.data[4] & 0x10) !== 0)
  return { chunks, alpha }
}

const u16 = (n) => [n & 0xff, (n >> 8) & 0xff]
const u24 = (n) => [n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff]
const u32 = (n) => [...u24(n), (n >>> 24) & 0xff]
const ascii = (s) => [...s].map((c) => c.charCodeAt(0))

const concat = (parts) => {
  const out = new Uint8Array(parts.reduce((sum, p) => sum + p.length, 0))
  parts.reduce((at, p) => {
    out.set(p, at)
    return at + p.length
  }, 0)
  return out
}

// header, data, and a pad byte when the data is odd-sized
const chunk = (fourcc, data) =>
  concat([ascii(fourcc), u32(data.length), data, data.length % 2 ? [0] : []])

const VP8X_ALPHA = 0x10
const VP8X_ANIMATION = 0x02
// blending off (bit 1), disposal none (bit 0)
const ANMF_NO_BLEND = 0x02

// An animated WebP of frames ({ chunks, alpha, duration } with duration in
// ms, all of the canvas size); loops 0 plays forever.
export const muxAnimatedWebP = ({ width, height, loops, frames }) => {
  const flags = VP8X_ANIMATION | (frames.some((f) => f.alpha) ? VP8X_ALPHA : 0)
  const vp8x = chunk('VP8X', [
    flags,
    0,
    0,
    0,
    ...u24(width - 1),
    ...u24(height - 1),
  ])
  const anim = chunk('ANIM', [0, 0, 0, 0, ...u16(loops)])
  // every frame covers the canvas: offset 0 (stored halved), full size
  const anmfs = frames.map(({ chunks, duration }) =>
    chunk(
      'ANMF',
      concat([
        u24(0),
        u24(0),
        u24(width - 1),
        u24(height - 1),
        u24(duration),
        [ANMF_NO_BLEND],
        ...chunks.map((c) => chunk(c.fourcc, c.data)),
      ]),
    ),
  )
  const body = concat([ascii('WEBP'), vp8x, anim, ...anmfs])
  return concat([ascii('RIFF'), u32(body.length), body])
}

// A frame's time in ms from a VideoFrame duration in µs. Browsers play a
// GIF delay of 10 ms or less (or none) at 100 ms; the field has 24 bits.
export const frameDuration = (microseconds) => {
  const ms = Math.round((microseconds ?? 0) / 1000)
  return ms <= 10 ? 100 : Math.min(ms, 0xffffff)
}

// The WebP loop count (plays, 0 = forever, 16 bits) from a GIF's
// repetition count as ImageDecoder gives it (repeats after the first play).
export const loopCount = (repetitions) =>
  repetitions === Infinity ? 0 : Math.min(repetitions + 1, 0xffff)
